import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";
import { PageHeader, FilterCard, Field, SimpleSelect, EmptyState, WIBClock, KpiCard } from "@/components/common";
import { fmtPct } from "@/lib/helpers";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  LayoutDashboard, Building2, TrendingUp, CheckCircle2, Database, AlertTriangle,
} from "lucide-react";
import { useIsNarrow, lineChartMargin, capaianLabels, targetEndLabel } from "@/components/chartLabels";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from "recharts";

export default function Dashboard() {
  const { indikators, tahuns } = useMeta();
  const [indikator, setIndikator] = useState("");
  const [tahun, setTahun] = useState("");
  const [departemen, setDepartemen] = useState("__all__");
  const [deps, setDeps] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!indikator && indikators.length) setIndikator(indikators[0].kode);
  }, [indikators]);
  useEffect(() => {
    if (!tahun && tahuns.length) setTahun(String(tahuns[0]));
  }, [tahuns]);

  useEffect(() => {
    if (indikator) api.get(`/departemen?indikator=${encodeURIComponent(indikator)}`).then((r) => setDeps(r.data));
  }, [indikator]);

  useEffect(() => {
    if (!indikator || !tahun) return;
    let url = `/dashboard/summary?indikator=${encodeURIComponent(indikator)}&tahun=${tahun}`;
    if (departemen !== "__all__") url += `&departemen=${encodeURIComponent(departemen)}`;
    api.get(url).then((r) => setData(r.data));
  }, [indikator, tahun, departemen]);

  const narrow = useIsNarrow();
  const chartData = (data?.trend || [])
    .filter((t) => t.ada_data || t.target != null)
    .map((t) => ({
      name: t.bulan_nama.slice(0, 3),
      Capaian: t.capaian != null ? +(t.capaian * 100).toFixed(1) : null,
      Target: t.target != null ? +(t.target * 100).toFixed(1) : null,
    }));

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        icon={LayoutDashboard}
        title="Dashboard Ringkasan"
        subtitle="Tren capaian vs target per bulan. Hijau = tercapai, merah = tidak tercapai."
      />

      <div className="-mt-2 mb-6">
        <WIBClock />
      </div>

      <FilterCard>
        <Field label="Indikator">
          <SimpleSelect
            testId="dash-indikator-select"
            value={indikator}
            onChange={setIndikator}
            options={indikators.map((i) => ({ label: i.kode, value: i.kode }))}
            placeholder="Pilih indikator"
            className="w-[220px]"
          />
        </Field>
        <Field label="Tahun">
          <SimpleSelect
            testId="dash-tahun-select"
            value={tahun}
            onChange={setTahun}
            options={tahuns.map((t) => ({ label: String(t), value: t }))}
            placeholder="Tahun"
            className="w-[120px]"
          />
        </Field>
        <Field label="Departemen">
          <SimpleSelect
            testId="dash-departemen-select"
            value={departemen}
            onChange={setDepartemen}
            options={[{ label: "Semua Departemen", value: "__all__" }, ...deps.map((d) => ({ label: d, value: d }))]}
            placeholder="Departemen"
            className="w-[220px]"
          />
        </Field>
      </FilterCard>

      {data && !data.kpi.has_target && (
        <Alert className="mb-6 border-amber-300 bg-amber-50" data-testid="target-warning">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Target tahun ini belum diisi</AlertTitle>
          <AlertDescription className="text-amber-700">
            Buka halaman <b>Kelola Target Tahunan</b> untuk mengisi target {indikator} tahun {tahun}.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard icon={Building2} label="Total Poliklinik" value={data?.kpi.total_poliklinik ?? "—"} tone="slate" />
        <KpiCard
          icon={CheckCircle2}
          label="Rasio Tercapai"
          value={data ? fmtPct(data.kpi.achieved_rate) : "—"}
          tone="emerald"
          sub="poliklinik memenuhi target"
        />
        <KpiCard icon={Database} label="Total Record" value={data?.kpi.total_records ?? "—"} tone="blue" />
        <KpiCard
          icon={TrendingUp}
          label="Bulan Berdata"
          value={data ? chartData.filter((c) => c.Capaian != null).length : "—"}
          tone="amber"
        />
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Tren Target vs Capaian — {indikator} {tahun}</CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <EmptyState icon={TrendingUp} title="Belum ada data" desc="Upload data bulanan untuk melihat tren." />
          ) : (
            <div style={{ width: "100%", height: 380 }} data-testid="dashboard-chart">
              <ResponsiveContainer>
                <LineChart data={chartData} margin={lineChartMargin(narrow, 0)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 12, fill: "#64748B" }} domain={[0, 100]} unit="%" />
                  <Tooltip formatter={(v) => (v != null ? `${v}%` : "N/A")} />
                  <Legend />
                  <Line type="monotone" dataKey="Target" stroke="hsl(var(--brand-lime))" strokeWidth={3} strokeDasharray="6 4" dot={false} connectNulls>
                    {targetEndLabel({ data: chartData, narrow })}
                  </Line>
                  <Line type="monotone" dataKey="Capaian" stroke="hsl(var(--brand-teal))" strokeWidth={3} dot={{ r: 4 }} connectNulls>
                    {capaianLabels({ data: chartData, narrow })}
                  </Line>
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
